import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { isOpenAlteration } from '@tailoring-crm/shared';
import { AlterationNextStep } from '../components/alterations/alteration-next-step.js';
import { AlterationPaymentsCard } from '../components/alterations/alteration-payments-card.js';
import { AlterationUpdatesCard } from '../components/alterations/alteration-updates-card.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge, Button, ListSkeleton } from '@tailoring-crm/ui';
import { useAlteration } from '../lib/alterations/use-alterations.js';
import { formatDate, formatDateLong, formatMobile } from '../lib/format.js';

/**
 * One alteration: what is wrong, whose it is, where it has got to, what it costs and what the
 * customer has been told.
 *
 * Its own page rather than a panel over the list, for the order page's reason: taking money and
 * previewing a message each open a dialog, and a dialog over a dialog fights it for the focus.
 * The day book and the customer timeline link here too, which a panel could not be linked to.
 *
 * A walk-in says so, and links nowhere; one booked back from an order links to that order.
 */
export function AlterationDetailPage(): ReactElement {
  const { t } = useTranslation();
  const { alterationId = '' } = useParams();
  const alteration = useAlteration(alterationId || null);

  if (alteration.isPending) {
    return (
      <>
        <PageHeader title={t('alterations.title')} />
        <ListSkeleton rows={3} />
      </>
    );
  }

  const data = alteration.data;
  if (!data) {
    return (
      <>
        <PageHeader title={t('alterations.title')} description={t('error.unexpected')} />
        <Button asChild variant="outline">
          <Link to="/alterations">{t('alterations.title')}</Link>
        </Button>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('alterations.detail.title', { number: data.number })}
        description={`${data.customerName} · ${formatMobile(data.customerPhone)}`}
        action={<AlterationNextStep alteration={data} size="default" />}
      />

      <div className="flex flex-col gap-5">
        <Link
          to="/alterations"
          className="inline-flex items-center gap-1 self-start text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {t('alterations.title')}
        </Link>

        <section className="flex flex-wrap items-center gap-2">
          <Badge variant={isOpenAlteration(data.status) ? 'default' : 'secondary'}>
            {t(`alterations.status.${data.status}`)}
          </Badge>
          {data.orderId ? null : <Badge variant="outline">{t('alterations.walkIn.badge')}</Badge>}
          <span className="text-sm text-muted-foreground">
            {t('alterations.detail.bookedOn', { date: formatDateLong(data.bookedAt) })}
          </span>
        </section>

        <dl className="grid grid-cols-1 gap-3 rounded-lg border border-border p-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs text-muted-foreground">{t('alterations.walkIn.garment')}</dt>
            <dd className="font-medium">{data.garmentTypeLabel}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t('alterations.field.promisedDate')}</dt>
            <dd className="font-medium">
              {data.promisedDate ? formatDate(data.promisedDate) : t('alterations.detail.noDate')}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs text-muted-foreground">{t('alterations.field.complaint')}</dt>
            <dd className="font-medium">{data.complaint}</dd>
          </div>
          {data.orderId ? (
            <div>
              <dt className="text-xs text-muted-foreground">{t('alterations.detail.fromOrder')}</dt>
              <dd>
                <Link to={`/orders/${data.orderId}`} className="font-medium hover:underline">
                  {data.orderNumber}
                </Link>
              </dd>
            </div>
          ) : null}
          {data.assignedToName ? (
            <div>
              <dt className="text-xs text-muted-foreground">
                {t('alterations.detail.assignedTo')}
              </dt>
              <dd className="font-medium">{data.assignedToName}</dd>
            </div>
          ) : null}
          {data.notes ? (
            <div className="sm:col-span-2">
              <dt className="text-xs text-muted-foreground">{t('alterations.field.notes')}</dt>
              <dd className="whitespace-pre-line">{data.notes}</dd>
            </div>
          ) : null}
          {data.cancelReason ? (
            <div className="sm:col-span-2">
              <dt className="text-xs text-muted-foreground">
                {t('alterations.detail.cancelReason')}
              </dt>
              <dd>{data.cancelReason}</dd>
            </div>
          ) : null}
        </dl>

        <AlterationPaymentsCard alteration={data} />
        <AlterationUpdatesCard alteration={data} />
      </div>
    </>
  );
}

export default AlterationDetailPage;
